import {useEffect, useRef, useState} from 'react';

import {classNames} from '../../../util/css';
import {getImageFromUrl} from '../../../util/image';

type PropsType = {
    baseImageSrc: string;
    logoUrl: string | undefined;
    className?: string;
    /** Logo position as fraction of image size (0–1). Default: top-left area on the "screen" */
    logoPosition?: {left: number; top: number};
    /** Logo width as fraction of image width (0–1). Height keeps logo aspect ratio. */
    logoWidthFraction?: number;
    /** Max logo height as fraction of image height (0–1). Scales logo down if needed. Default: 0.05 */
    logoMaxHeightFraction?: number;
};

type DrawOptionsType = {
    positionLeft: number;
    positionTop: number;
    logoWidthFraction: number;
    logoMaxHeightFraction: number;
};

function computeLogoSize(
    imageWidth: number,
    imageHeight: number,
    logoNaturalWidth: number,
    logoNaturalHeight: number,
    options: DrawOptionsType
): {width: number; height: number} {
    const {logoWidthFraction, logoMaxHeightFraction} = options;

    let width = imageWidth * logoWidthFraction;
    let height = width * (logoNaturalHeight / logoNaturalWidth);

    const maxHeight = imageHeight * logoMaxHeightFraction;

    if (height > maxHeight) {
        height = maxHeight;
        width = height * (logoNaturalWidth / logoNaturalHeight);
    }

    return {width, height};
}

export function LandingImageWithLogo(props: PropsType): JSX.Element {
    const {
        baseImageSrc,
        logoUrl,
        className,
        logoPosition = {left: 0.17, top: 0.25},
        logoWidthFraction = 0.13,
        logoMaxHeightFraction = 0.05,
    } = props;

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [fallbackToImg, setFallbackToImg] = useState(false);

    const positionLeft = logoPosition.left;
    const positionTop = logoPosition.top;

    useEffect(() => {
        let cancelled = false;

        if (!logoUrl) {
            setFallbackToImg(true);

            return () => {
                cancelled = true;
            };
        }

        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d');

        if (!canvas || !context) {
            setFallbackToImg(true);

            return () => {
                cancelled = true;
            };
        }

        Promise.all([getImageFromUrl(baseImageSrc), getImageFromUrl(logoUrl)])
            .then(([baseImg, logoImg]) => {
                if (cancelled) {
                    return;
                }

                const imageWidth = baseImg.naturalWidth;
                const imageHeight = baseImg.naturalHeight;

                canvas.width = imageWidth;
                canvas.height = imageHeight;
                context.drawImage(baseImg, 0, 0);

                const drawOptions: DrawOptionsType = {
                    positionLeft,
                    positionTop,
                    logoWidthFraction,
                    logoMaxHeightFraction,
                };
                const {width, height} = computeLogoSize(
                    imageWidth,
                    imageHeight,
                    logoImg.naturalWidth,
                    logoImg.naturalHeight,
                    drawOptions
                );

                context.drawImage(logoImg, imageWidth * positionLeft, imageHeight * positionTop, width, height);
            })
            .catch(() => {
                if (!cancelled) {
                    setFallbackToImg(true);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [baseImageSrc, logoUrl, positionLeft, positionTop, logoWidthFraction, logoMaxHeightFraction]);

    if (fallbackToImg || !logoUrl) {
        return <img alt="" className={classNames(className)} src={baseImageSrc} />;
    }

    return <canvas className={classNames(className)} ref={canvasRef} />;
}
